import { useState, useEffect, useMemo, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const C = { bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040", border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96", dim: "#384660", blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981", purple: "#8b5cf6", cyan: "#06b6d4", red: "#ef4444", pink: "#ec4899", teal: "#14b8a6", orange: "#f97316", lime: "#84cc16", white: "#fff", indigo: "#6366f1", rose: "#f43f5e" };

const ESRS_META = { E1: { l: "Climate Change", c: C.emerald }, E2: { l: "Pollution", c: C.orange }, E3: { l: "Water & Marine", c: C.cyan }, E4: { l: "Biodiversity", c: C.lime }, E5: { l: "Resource Use", c: C.teal }, S1: { l: "Own Workforce", c: C.blue }, S2: { l: "Value Chain Workers", c: C.indigo }, S3: { l: "Communities", c: C.pink }, S4: { l: "Consumers", c: C.rose }, G1: { l: "Business Conduct", c: C.purple } };
const FW_COLORS = { gri: C.emerald, tcfd: C.blue, cdp: C.amber, sbti: C.red, tnfd: C.lime, sasb: C.indigo, ghgp: C.teal, issb: C.purple };
const ST = { not_started: { l: "Not Started", c: C.dim }, collecting: { l: "Collecting", c: C.blue }, in_review: { l: "In Review", c: C.amber }, approved: { l: "Approved", c: C.emerald }, published: { l: "Published", c: C.teal } };
const VIEWS = [
  { id: "flywheel", l: "Flywheel", icon: "\u21BB" },
  { id: "iros", l: "IRO Register", icon: "\u2696" },
  { id: "cascade", l: "Cascade", icon: "\u26A1" },
  { id: "gaps", l: "Gap Analysis", icon: "\uD83D\uDD0D" },
  { id: "audit", l: "Audit Trail", icon: "\uD83D\uDEE1" },
];

function getToken() { return localStorage.getItem("token") || ""; }
function hdrs() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }
async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, { ...opts, headers: { ...hdrs(), ...opts.headers } });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

// ─── Small UI primitives ──────────────────────────────────────────
const Pill = ({ color, children, small, glow, onClick, "data-testid": tid }) => (
  <span data-testid={tid} onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: small ? "1px 6px" : "3px 9px", borderRadius: 99, fontSize: small ? 8 : 10, fontWeight: 600, background: color + "15", color, border: `1px solid ${color}33`, cursor: onClick ? "pointer" : "default", boxShadow: glow ? `0 0 8px ${color}33` : "none", transition: "all .2s" }}>{children}</span>
);
const Bar = ({ v, mx, cl, h = 5 }) => (
  <div style={{ width: "100%", height: h, borderRadius: 99, background: C.border, overflow: "hidden" }}>
    <div style={{ width: Math.min((v / mx) * 100, 100) + "%", height: "100%", borderRadius: 99, background: `linear-gradient(90deg,${cl}88,${cl})`, transition: "width .4s" }} />
  </div>
);
const Kpi = ({ label, value, color, sub }) => (
  <div style={{ background: C.card, borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.border}` }}>
    <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: color || C.text }}>{value}</div>
    {sub && <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function MaterialityConnector() {
  const [view, setView] = useState("flywheel");
  const [iros, setIros] = useState([]);
  const [datapoints, setDatapoints] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selIro, setSelIro] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [irosRes, dpsRes, logRes] = await Promise.all([
        apiFetch("/api/v1/materiality/iros"),
        apiFetch("/api/v1/materiality-connector/datapoints"),
        apiFetch("/api/v1/materiality-connector/audit-log?limit=100"),
      ]);
      setIros(irosRes.iros || []);
      setDatapoints(dpsRes.datapoints || []);
      setAuditLog(logRes.entries || []);
    } catch (e) { console.error("Fetch error:", e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Computed ───────────────────────────────────────────────────
  const materialIros = useMemo(() => iros.filter(i => i.double_material), [iros]);
  const nonMaterialIros = useMemo(() => iros.filter(i => !i.double_material), [iros]);
  const matEsrsCodes = useMemo(() => new Set(materialIros.map(i => i.esrs)), [materialIros]);

  const activeDps = useMemo(() => datapoints.filter(d => matEsrsCodes.has(d.esrs)), [datapoints, matEsrsCodes]);
  const dormantDps = useMemo(() => datapoints.filter(d => !matEsrsCodes.has(d.esrs)), [datapoints, matEsrsCodes]);

  const fwReqCount = useMemo(() => activeDps.reduce((s, d) => s + Object.keys(d.framework_map || {}).length, 0), [activeDps]);
  const gapsNotStarted = useMemo(() => activeDps.filter(d => d.status === "not_started"), [activeDps]);
  const gapsLowQuality = useMemo(() => activeDps.filter(d => d.quality > 0 && d.quality < 3), [activeDps]);
  const gapsNoEvidence = useMemo(() => activeDps.filter(d => !(d.evidence_ids && d.evidence_ids.length)), [activeDps]);

  // ─── Verdict toggle ─────────────────────────────────────────────
  const toggleVerdict = async (iro) => {
    const newVal = iro.double_material ? "not_material" : "material";
    try {
      await apiFetch(`/api/v1/materiality/iros/${iro.id}`, {
        method: "PUT", body: JSON.stringify({ verdict_override: newVal }),
      });
      await apiFetch("/api/v1/materiality-connector/audit-log", {}); // just to trigger server log
      await fetchAll();
    } catch (e) { console.error("Toggle error:", e); }
  };

  // ─── Datapoint status update ────────────────────────────────────
  const updateDpStatus = async (dpId, newStatus) => {
    try {
      await apiFetch(`/api/v1/materiality-connector/datapoints/${dpId}`, {
        method: "PUT", body: JSON.stringify({ status: newStatus }),
      });
      await fetchAll();
    } catch (e) { console.error("Update error:", e); }
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: C.muted, fontSize: 14 }}>Loading Defensibility Flywheel...</div>
    </div>
  );

  return (
    <div data-testid="materiality-connector" style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'Inter','SF Pro',-apple-system,sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "24px 32px 0", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: C.emerald + "18", border: `1px solid ${C.emerald}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{"\u21BB"}</div>
          <div>
            <h1 data-testid="connector-title" style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Materiality Connector</h1>
            <div style={{ fontSize: 11, color: C.muted }}>Defensibility Flywheel: IRO verdicts cascade to framework disclosures</div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <Pill color={C.emerald} glow>{materialIros.length} material IROs</Pill>
            <Pill color={C.blue}>{activeDps.length} active datapoints</Pill>
            <Pill color={C.amber}>{fwReqCount} FW requirements</Pill>
          </div>
        </div>
        {/* Tab bar */}
        <div data-testid="view-tabs" style={{ display: "flex", gap: 2 }}>
          {VIEWS.map(v => (
            <button key={v.id} data-testid={`tab-${v.id}`} onClick={() => setView(v.id)}
              style={{ padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", background: view === v.id ? C.card : "transparent", color: view === v.id ? C.emerald : C.muted, border: "none", borderBottom: view === v.id ? `2px solid ${C.emerald}` : "2px solid transparent", borderRadius: "6px 6px 0 0", transition: "all .2s" }}>
              <span style={{ marginRight: 6 }}>{v.icon}</span>{v.l}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "24px 32px" }}>
        {view === "flywheel" && <FlywheelView materialIros={materialIros} activeDps={activeDps} dormantDps={dormantDps} fwReqCount={fwReqCount} gaps={{ notStarted: gapsNotStarted.length, lowQuality: gapsLowQuality.length, noEvidence: gapsNoEvidence.length }} />}
        {view === "iros" && <IRORegisterView iros={iros} materialIros={materialIros} datapoints={datapoints} matEsrsCodes={matEsrsCodes} selIro={selIro} setSelIro={setSelIro} toggleVerdict={toggleVerdict} />}
        {view === "cascade" && <CascadeView materialIros={materialIros} datapoints={datapoints} matEsrsCodes={matEsrsCodes} />}
        {view === "gaps" && <GapAnalysisView notStarted={gapsNotStarted} lowQuality={gapsLowQuality} noEvidence={gapsNoEvidence} updateDpStatus={updateDpStatus} />}
        {view === "audit" && <AuditTrailView auditLog={auditLog} iros={iros} setView={setView} setSelIro={setSelIro} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VIEW 1: FLYWHEEL
// ═══════════════════════════════════════════════════════════════════
function FlywheelView({ materialIros, activeDps, dormantDps, fwReqCount, gaps }) {
  const approvedDps = activeDps.filter(d => d.status === "approved" || d.status === "published");
  const steps = [
    { label: "Material IROs", count: materialIros.length, color: C.purple, sub: "Double materiality verdicts" },
    { label: "Activated DPs", count: activeDps.length, color: C.blue, sub: "Datapoints triggered" },
    { label: "FW Requirements", count: fwReqCount, color: C.amber, sub: "Cross-framework mapping" },
    { label: "Gaps Found", count: gaps.notStarted + gaps.lowQuality + gaps.noEvidence, color: C.red, sub: "Blocking completion" },
    { label: "Audit Events", count: approvedDps.length + " approved", color: C.emerald, sub: "Immutable trail" },
  ];

  return (
    <div data-testid="flywheel-view">
      {/* 5-step flow */}
      <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 28, overflowX: "auto" }}>
        {steps.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ background: C.card, borderRadius: 12, padding: "18px 22px", border: `1px solid ${s.color}33`, minWidth: 160, textAlign: "center" }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.text, marginTop: 2 }}>{s.label}</div>
              <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{s.sub}</div>
            </div>
            {i < steps.length - 1 && <div style={{ width: 40, height: 2, background: `linear-gradient(90deg,${s.color}66,${steps[i + 1].color}66)`, margin: "0 -1px", flexShrink: 0 }} />}
          </div>
        ))}
      </div>

      {/* KPI Grid */}
      <div data-testid="flywheel-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 28 }}>
        <Kpi label="Material IROs" value={materialIros.length} color={C.purple} sub={`of ${materialIros.length + dormantDps.length > 0 ? "total assessed" : "0"}`} />
        <Kpi label="Active Datapoints" value={activeDps.length} color={C.blue} sub={`${dormantDps.length} dormant`} />
        <Kpi label="Approved" value={approvedDps.length} color={C.emerald} sub={`${activeDps.length ? Math.round(approvedDps.length / activeDps.length * 100) : 0}% ready`} />
        <Kpi label="Total Gaps" value={gaps.notStarted + gaps.lowQuality + gaps.noEvidence} color={C.red} sub="blocking disclosure" />
        <Kpi label="Not Started" value={gaps.notStarted} color={C.red} sub="no data collection" />
        <Kpi label="Low Quality" value={gaps.lowQuality} color={C.amber} sub="quality < 3/5" />
        <Kpi label="No Evidence" value={gaps.noEvidence} color={C.teal} sub="indefensible in audit" />
        <Kpi label="FW Requirements" value={fwReqCount} color={C.amber} sub="across all frameworks" />
      </div>

      {/* Dormant section */}
      {dormantDps.length > 0 && (
        <div style={{ background: C.card, borderRadius: 10, padding: 16, border: `1px solid ${C.border}`, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 8 }}>Dormant Datapoints ({dormantDps.length})</div>
          <div style={{ fontSize: 10, color: C.dim, marginBottom: 10 }}>These datapoints exist but are not activated because their ESRS topic is not currently material. They would spring to life if the materiality verdict changes.</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {dormantDps.map(d => (
              <Pill key={d.id} color={C.dim} small>{d.name} ({d.esrs})</Pill>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      <div data-testid="flywheel-howto" style={{ background: C.surface, borderRadius: 10, padding: 20, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>How the Defensibility Flywheel Works</div>
        {[
          { n: "1", t: "Materiality Assessment", d: "IROs scored on Impact (inside-out) and Financial (outside-in) dimensions. Threshold: score >= 9 = material." },
          { n: "2", t: "Standard Activation", d: "Material IROs auto-activate their ESRS topical standard (E1-E5, S1-S4, G1)." },
          { n: "3", t: "Datapoint Cascade", d: "Each activated standard triggers specific datapoints that must be collected and disclosed." },
          { n: "4", t: "Framework Mapping", d: "Datapoints auto-map to voluntary frameworks (GRI, TCFD, CDP, SBTi, etc.) with specific disclosure codes." },
          { n: "5", t: "Audit Trail", d: "Every decision, activation, and approval is logged immutably. Auditors navigate forward or backward through the chain." },
        ].map(s => (
          <div key={s.n} style={{ display: "flex", gap: 12, marginBottom: 10, alignItems: "flex-start" }}>
            <div style={{ width: 24, height: 24, borderRadius: 99, background: C.emerald + "22", color: C.emerald, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{s.n}</div>
            <div><div style={{ fontSize: 11, fontWeight: 600 }}>{s.t}</div><div style={{ fontSize: 10, color: C.muted }}>{s.d}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VIEW 2: IRO REGISTER
// ═══════════════════════════════════════════════════════════════════
function IRORegisterView({ iros, materialIros, datapoints, matEsrsCodes, selIro, setSelIro, toggleVerdict }) {
  const selected = selIro || iros[0];
  const isMat = selected?.double_material;
  const linkedDps = datapoints.filter(d => d.esrs === selected?.esrs);
  const activeDps = linkedDps.filter(d => matEsrsCodes.has(d.esrs));
  const dormantLinked = linkedDps.filter(d => !matEsrsCodes.has(d.esrs));
  const esrsMeta = ESRS_META[selected?.esrs] || { l: selected?.esrs, c: C.muted };

  return (
    <div data-testid="iro-register-view" style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 20, minHeight: 600 }}>
      {/* Left: IRO List */}
      <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "auto", maxHeight: 700 }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, color: C.muted }}>
          IRO Register ({iros.length})
        </div>
        {iros.map(iro => {
          const meta = ESRS_META[iro.esrs] || { l: iro.esrs, c: C.muted };
          const dpCount = datapoints.filter(d => d.esrs === iro.esrs).length;
          const isActive = selected?.id === iro.id;
          return (
            <div key={iro.id} data-testid={`iro-item-${iro.id}`} onClick={() => setSelIro(iro)}
              style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, cursor: "pointer", background: isActive ? C.cardHover : "transparent", transition: "background .15s", borderLeft: isActive ? `3px solid ${meta.c}` : "3px solid transparent" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <Pill color={meta.c} small>{iro.esrs}</Pill>
                <Pill color={iro.double_material ? C.emerald : C.dim} small glow={iro.double_material}>
                  {iro.double_material ? "MATERIAL" : "Not Material"}
                </Pill>
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.text, marginBottom: 4, lineHeight: 1.3 }}>{iro.name}</div>
              <div style={{ display: "flex", gap: 8, fontSize: 9 }}>
                <span style={{ color: C.red }}>Impact: {iro.impact_score}</span>
                <span style={{ color: C.blue }}>Financial: {iro.financial_score}</span>
                <span style={{ color: C.muted }}>{dpCount} DPs</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Right: IRO Detail */}
      {selected && (
        <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20, overflow: "auto", maxHeight: 700 }}>
          {/* IRO Header */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: esrsMeta.c + "18", border: `1px solid ${esrsMeta.c}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: esrsMeta.c }}>{selected.esrs}</div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, marginBottom: 4 }}>{selected.name}</h3>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Pill color={esrsMeta.c}>{esrsMeta.l}</Pill>
                <Pill color={C.muted}>{selected.type}</Pill>
                {selected.owner && <Pill color={C.blue}>{selected.owner}</Pill>}
              </div>
            </div>
          </div>

          {/* Verdict Toggle */}
          <button data-testid="verdict-toggle" onClick={() => toggleVerdict(selected)}
            style={{ width: "100%", padding: "10px 16px", marginBottom: 16, borderRadius: 8, border: `1px solid ${isMat ? C.emerald : C.dim}55`, background: isMat ? C.emerald + "12" : C.surface, color: isMat ? C.emerald : C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all .2s", textAlign: "center" }}>
            {isMat ? "\u2713 MATERIAL \u2014 click to change to not material" : "\u2717 NOT MATERIAL \u2014 click to change to material"}
          </button>

          {/* Double Materiality Scores */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ background: C.red + "08", borderRadius: 8, padding: 12, border: `1px solid ${C.red}22` }}>
              <div style={{ fontSize: 10, color: C.red, fontWeight: 600, marginBottom: 6 }}>Impact Materiality (inside-out)</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.red }}>{selected.impact_score}<span style={{ fontSize: 10, color: C.muted }}>/25</span></div>
              <Bar v={selected.impact_score} mx={25} cl={C.red} h={4} />
              <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>
                Severity {selected.impact_severity || 0} x Likelihood {selected.impact_likelihood || 0} = {selected.impact_score}
                {selected.impact_score >= 9 ? <span style={{ color: C.emerald, marginLeft: 6 }}>{"\u2713"} above threshold</span> : <span style={{ color: C.dim, marginLeft: 6 }}>below 9</span>}
              </div>
            </div>
            <div style={{ background: C.blue + "08", borderRadius: 8, padding: 12, border: `1px solid ${C.blue}22` }}>
              <div style={{ fontSize: 10, color: C.blue, fontWeight: 600, marginBottom: 6 }}>Financial Materiality (outside-in)</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.blue }}>{selected.financial_score}<span style={{ fontSize: 10, color: C.muted }}>/25</span></div>
              <Bar v={selected.financial_score} mx={25} cl={C.blue} h={4} />
              <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>
                Magnitude {selected.financial_magnitude || 0} x Probability {selected.financial_probability || 0} = {selected.financial_score}
                {selected.financial_score >= 9 ? <span style={{ color: C.emerald, marginLeft: 6 }}>{"\u2713"} above threshold</span> : <span style={{ color: C.dim, marginLeft: 6 }}>below 9</span>}
              </div>
            </div>
          </div>

          {/* Activated Datapoints */}
          {isMat && activeDps.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.emerald, marginBottom: 8 }}>Activated Datapoints ({activeDps.length})</div>
              {activeDps.map(dp => (
                <DpRow key={dp.id} dp={dp} />
              ))}
            </div>
          )}

          {/* Dormant Datapoints */}
          {!isMat && dormantLinked.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.dim, marginBottom: 4 }}>Dormant Datapoints ({dormantLinked.length})</div>
              <div style={{ fontSize: 10, color: C.dim, marginBottom: 8 }}>Would activate if this IRO becomes material</div>
              {dormantLinked.map(dp => (
                <DpRow key={dp.id} dp={dp} dormant />
              ))}
            </div>
          )}

          {isMat && activeDps.length === 0 && dormantLinked.length === 0 && (
            <div style={{ fontSize: 11, color: C.muted, padding: 16, textAlign: "center", background: C.surface, borderRadius: 8 }}>
              No datapoints linked to this ESRS topic yet. Use "Generate from IROs" to create them.
            </div>
          )}

          {/* ESRS Activation */}
          {selected.framework_triggers && (
            <div style={{ background: C.surface, borderRadius: 8, padding: 12, marginTop: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 6 }}>Framework Triggers ({selected.esrs})</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {Object.entries(selected.framework_triggers).map(([fw, refs]) =>
                  refs.map((ref, i) => (
                    <Pill key={`${fw}-${i}`} color={FW_COLORS[fw] || C.muted} small>{fw.toUpperCase()}: {ref}</Pill>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Shared datapoint row ─────────────────────────────────────────
function DpRow({ dp, dormant }) {
  const stMeta = ST[dp.status] || ST.not_started;
  return (
    <div data-testid={`dp-row-${dp.id}`} style={{ background: dormant ? C.surface : C.cardHover, borderRadius: 8, padding: "10px 14px", marginBottom: 6, border: `1px solid ${dormant ? C.border : stMeta.c + "33"}`, opacity: dormant ? 0.5 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{dp.name}</div>
        <Pill color={stMeta.c} small>{stMeta.l}</Pill>
        {dp.quality > 0 && <Pill color={dp.quality >= 4 ? C.emerald : dp.quality >= 3 ? C.amber : C.red} small>Q{dp.quality}/5</Pill>}
      </div>
      {dp.value && <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{dp.value} {dp.unit} | {dp.period}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {(dp.frameworks || []).map(fw => (
          <Pill key={fw} color={FW_COLORS[fw] || C.muted} small>
            {fw.toUpperCase()}{dp.framework_map?.[fw] ? `: ${dp.framework_map[fw]}` : ""}
          </Pill>
        ))}
        {dp.esrs_ref && <Pill color={ESRS_META[dp.esrs]?.c || C.muted} small>ESRS {dp.esrs_ref}</Pill>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VIEW 3: CASCADE
// ═══════════════════════════════════════════════════════════════════
function CascadeView({ materialIros, datapoints, matEsrsCodes }) {
  // Group by ESRS to avoid duplicate sections for same-standard IROs
  const esrsGroups = {};
  materialIros.forEach(iro => {
    const code = iro.esrs;
    if (!esrsGroups[code]) esrsGroups[code] = { iros: [], dps: [] };
    esrsGroups[code].iros.push(iro);
  });
  Object.keys(esrsGroups).forEach(code => {
    esrsGroups[code].dps = datapoints.filter(d => d.esrs === code);
  });

  return (
    <div data-testid="cascade-view">
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
        Complete propagation: Material IRO {"\u2192"} Activated Datapoints {"\u2192"} Framework Requirements
      </div>
      {Object.entries(esrsGroups).map(([code, group]) => {
        const meta = ESRS_META[code] || { l: code, c: C.muted };
        const readyDps = group.dps.filter(d => d.status === "approved" || d.status === "published");
        const totalFwReqs = group.dps.reduce((s, d) => s + Object.keys(d.framework_map || {}).length, 0);
        return (
          <div key={code} data-testid={`cascade-${code}`} style={{ background: C.card, borderRadius: 12, border: `1px solid ${meta.c}33`, padding: 16, marginBottom: 16 }}>
            {/* IRO Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: meta.c + "18", border: `1px solid ${meta.c}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: meta.c }}>{code}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{meta.l}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{group.iros.map(i => i.name).join(" | ")}</div>
              </div>
              <Pill color={C.emerald} glow>MATERIAL</Pill>
            </div>
            {/* Datapoints */}
            {group.dps.map(dp => (
              <DpRow key={dp.id} dp={dp} />
            ))}
            {/* Summary */}
            <div style={{ display: "flex", gap: 12, marginTop: 10, padding: "8px 12px", background: C.surface, borderRadius: 6, fontSize: 10 }}>
              <span style={{ color: C.muted }}>{group.dps.length} datapoints</span>
              <span style={{ color: C.muted }}>{"\u2192"}</span>
              <span style={{ color: C.amber }}>{totalFwReqs} framework requirements</span>
              <span style={{ color: C.muted }}>{"\u2192"}</span>
              <span style={{ color: readyDps.length === group.dps.length ? C.emerald : C.amber }}>
                {readyDps.length}/{group.dps.length} ready
                {readyDps.length < group.dps.length && <span style={{ color: C.red, marginLeft: 4 }}>{"\u26A0"} {group.dps.length - readyDps.length} gap(s)</span>}
              </span>
            </div>
          </div>
        );
      })}
      {Object.keys(esrsGroups).length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>
          No material IROs found. Score IROs in the Materiality Intelligence module to activate the cascade.
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VIEW 4: GAP ANALYSIS + AI REMEDIATION ADVISOR
// ═══════════════════════════════════════════════════════════════════
function GapAnalysisView({ notStarted, lowQuality, noEvidence, updateDpStatus }) {
  const [aiLoading, setAiLoading] = useState(null);
  const [aiResults, setAiResults] = useState({});
  const allClear = notStarted.length === 0 && lowQuality.length === 0 && noEvidence.length === 0;

  const getAiRemediation = async (dp, gapType) => {
    setAiLoading(dp.id);
    try {
      const res = await apiFetch("/api/v1/materiality-connector/ai-remediation", {
        method: "POST",
        body: JSON.stringify({
          name: dp.name, esrs: dp.esrs, esrs_ref: dp.esrs_ref || "",
          status: dp.status, value: dp.value || "", quality: dp.quality || 0,
          owner: dp.owner || "", methodology: dp.methodology || "",
          frameworks: dp.frameworks || [], framework_map: dp.framework_map || {},
          evidence_ids: dp.evidence_ids || [], gapType,
        }),
      });
      setAiResults(prev => ({ ...prev, [dp.id]: res }));
    } catch (e) {
      setAiResults(prev => ({ ...prev, [dp.id]: { success: false, error: e.message } }));
    }
    setAiLoading(null);
  };

  const AiPanel = ({ dpId }) => {
    const r = aiResults[dpId];
    if (!r) return null;
    return (
      <div data-testid={`ai-remediation-${dpId}`} style={{ marginTop: 8, background: C.surface, borderRadius: 8, padding: 12, border: `1px solid ${C.purple}33` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.purple }}>AI Remediation Advisor</div>
          {r.success === false && <Pill color={C.red} small>Error</Pill>}
        </div>
        {r.dataSources && (<div style={{ marginBottom: 8 }}><div style={{ fontSize: 9, fontWeight: 700, color: C.blue, marginBottom: 3 }}>Data Sources</div>{(Array.isArray(r.dataSources) ? r.dataSources : [r.dataSources]).map((s, i) => <div key={i} style={{ fontSize: 9, color: C.text, marginBottom: 1 }}>{"\u2022"} {s}</div>)}</div>)}
        {r.methodology && (<div style={{ marginBottom: 8 }}><div style={{ fontSize: 9, fontWeight: 700, color: C.emerald, marginBottom: 3 }}>Methodology</div><div style={{ fontSize: 9, color: C.text }}>{r.methodology}</div></div>)}
        {r.evidenceRequired && (<div style={{ marginBottom: 8 }}><div style={{ fontSize: 9, fontWeight: 700, color: C.teal, marginBottom: 3 }}>Evidence Required</div>{(Array.isArray(r.evidenceRequired) ? r.evidenceRequired : [r.evidenceRequired]).map((s, i) => <div key={i} style={{ fontSize: 9, color: C.text, marginBottom: 1 }}>{"\u2022"} {s}</div>)}</div>)}
        {r.priorityActions && (<div style={{ marginBottom: 8 }}><div style={{ fontSize: 9, fontWeight: 700, color: C.amber, marginBottom: 3 }}>Priority Actions</div>{(Array.isArray(r.priorityActions) ? r.priorityActions : [r.priorityActions]).map((s, i) => <div key={i} style={{ fontSize: 9, color: C.text, marginBottom: 1 }}>{i + 1}. {s}</div>)}</div>)}
        <div style={{ display: "flex", gap: 12, fontSize: 9 }}>
          {r.qualityTarget && <span style={{ color: C.muted }}>Target Quality: <span style={{ color: C.emerald }}>{r.qualityTarget}</span></span>}
          {r.estimatedEffort && <span style={{ color: C.muted }}>Effort: <span style={{ color: C.amber }}>{r.estimatedEffort}</span></span>}
          {r.riskIfUnresolved && <span style={{ color: C.muted }}>Risk: <span style={{ color: C.red }}>{r.riskIfUnresolved}</span></span>}
        </div>
      </div>
    );
  };

  const GapSection = ({ title, icon, color, items, sub, gapType }) => (
    items.length > 0 && (
      <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${color}33`, padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          <div style={{ fontSize: 13, fontWeight: 700, color }}>{title} ({items.length})</div>
        </div>
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 10 }}>{sub}</div>
        {items.map(dp => {
          const meta = ESRS_META[dp.esrs] || { l: dp.esrs, c: C.muted };
          return (
            <div key={dp.id} data-testid={`gap-${dp.id}`} style={{ background: C.surface, borderRadius: 8, padding: "10px 14px", marginBottom: 6, border: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <Pill color={meta.c} small>{dp.esrs}</Pill>
                <div style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{dp.name}</div>
                <Pill color={ST[dp.status]?.c || C.dim} small>{ST[dp.status]?.l || dp.status}</Pill>
              </div>
              <div style={{ fontSize: 10, color: C.muted }}>
                Owner: {dp.owner || "Unassigned"} | IRO: {dp.iro_name || "N/A"} | Frameworks: {(dp.frameworks || []).map(f => f.toUpperCase()).join(", ")}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                {dp.status === "not_started" && (
                  <button data-testid={`start-collection-${dp.id}`} onClick={() => updateDpStatus(dp.id, "collecting")}
                    style={{ padding: "4px 12px", fontSize: 10, fontWeight: 600, borderRadius: 6, border: `1px solid ${C.blue}44`, background: C.blue + "12", color: C.blue, cursor: "pointer" }}>
                    Start Collection
                  </button>
                )}
                <button data-testid={`ai-advise-${dp.id}`} onClick={() => getAiRemediation(dp, gapType)}
                  disabled={aiLoading === dp.id}
                  style={{ padding: "4px 12px", fontSize: 10, fontWeight: 600, borderRadius: 6, border: `1px solid ${C.purple}44`, background: C.purple + "12", color: C.purple, cursor: aiLoading === dp.id ? "wait" : "pointer", opacity: aiLoading === dp.id ? 0.6 : 1 }}>
                  {aiLoading === dp.id ? "Analyzing..." : "AI Remediation"}
                </button>
              </div>
              <AiPanel dpId={dp.id} />
            </div>
          );
        })}
      </div>
    )
  );

  return (
    <div data-testid="gap-analysis-view">
      {allClear ? (
        <div style={{ textAlign: "center", padding: 60, background: C.card, borderRadius: 12, border: `1px solid ${C.emerald}33` }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{"\u2713"}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.emerald, marginBottom: 6 }}>All Clear</div>
          <div style={{ fontSize: 12, color: C.muted }}>All activated datapoints are approved. Your materiality-driven disclosures are complete across all frameworks.</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
            {notStarted.length + lowQuality.length + noEvidence.length} gaps blocking full disclosure readiness
          </div>
          <GapSection title="NOT STARTED" icon={"\uD83D\uDEA8"} color={C.red} items={notStarted} sub="Data collection hasn't begun. These datapoints are activated by material IROs but have no value." gapType="not_started" />
          <GapSection title="LOW QUALITY" icon={"\u26A0\uFE0F"} color={C.amber} items={lowQuality} sub="Data exists but quality is below threshold (< 3/5). Auditors will flag these." gapType="low_quality" />
          <GapSection title="NO EVIDENCE" icon={"\uD83D\uDCCE"} color={C.teal} items={noEvidence} sub="Even if values are entered, without supporting evidence these datapoints are indefensible in audit." gapType="no_evidence" />
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VIEW 5: AUDIT TRAIL
// ═══════════════════════════════════════════════════════════════════
function AuditTrailView({ auditLog, iros, setView, setSelIro }) {
  const actionColors = {
    created_datapoint: C.blue, approved_datapoint: C.emerald, updated_datapoint: C.amber,
    deleted_datapoint: C.red, verdict_changed: C.purple, cascade_activated: C.cyan,
    quality_flag: C.orange,
  };

  const navigateToIro = (entry) => {
    const iro = iros.find(i => i.id === entry.entity_id || i.name?.includes(entry.details?.split("'")[1]));
    if (iro) { setSelIro(iro); setView("iros"); }
  };

  return (
    <div data-testid="audit-trail-view">
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
        Immutable log of all materiality decisions and cascade events ({auditLog.length} entries)
      </div>
      <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        {auditLog.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 12 }}>No audit events yet.</div>
        ) : auditLog.map((entry, i) => {
          const actionColor = actionColors[entry.action] || C.muted;
          const ts = entry.timestamp ? new Date(entry.timestamp).toLocaleString() : "";
          return (
            <div key={entry.id || i} data-testid={`audit-entry-${i}`}
              style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer", transition: "background .15s" }}
              onClick={() => navigateToIro(entry)} onMouseOver={e => e.currentTarget.style.background = C.cardHover} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ width: 8, height: 8, borderRadius: 99, background: actionColor, marginTop: 5, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{entry.actor}</span>
                  <Pill color={actionColor} small>{(entry.action || "").replace(/_/g, " ")}</Pill>
                </div>
                <div style={{ fontSize: 10, color: C.muted }}>{entry.details}</div>
              </div>
              <div style={{ fontSize: 9, color: C.dim, whiteSpace: "nowrap" }}>{ts}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
